import RaffleServiceError, { ErrorCode } from '@frontend/services/raffles/exceptions/RaffleServiceError';
import Axios, { AxiosError, AxiosInstance, AxiosResponse } from 'axios';

export type Consent = 'termsAndConditions' | 'privacyPolicy';

interface ParticipationResponse {
	errorCode?: ErrorCode | string;
	message: string | null;
}

export interface AnswerStatistics {
	answer: string | number;
	share: number;
}

export interface IRaffle {
	/**
	 * @title Raffle id
	 */
	id: number;

	/**
	 * Participants will only be accepted within this period
	 * @title Participation period
	 */
	participationPeriod: {
		/**
		 * @title Start
		 */
		startsAt: string;
		/**
		 * @title End
		 */
		endsAt: string;
	};
}

export default class RaffleService {
	private clientInstance: AxiosInstance | undefined;

	/**
	 * Participate in a raffle
	 *
	 * @param raffleId
	 * @param email
	 * @param answers
	 * @param consents
	 * @param captchaSolution
	 */
	public async participate(
		raffleId: string,
		email: string,
		answers: string[],
		consents: Consent[],
		captchaSolution?: string
	): Promise<void> {
		try {
			if (RaffleService.useMock()) {
				await this.client.get('/raffle/participate.json');
			} else {
				await this.client.post<ParticipationResponse>(
					`/consumer/raffles/${raffleId}/participations`,
					{
						email,
						answers,
						captchaSolution
					},
					{
						validateStatus: (status: number): boolean => status === 201
					}
				);
			}
		} catch (error) {
			RaffleService.handleParticipationResponseError(error);
		}
	}

	/**
	 * Retrieve statistics about participation in a raffle
	 *
	 * @param raffleId
	 */
	public async getStatistics(raffleId: string): Promise<AnswerStatistics[]> {
		if (RaffleService.useMock()) {
			const response = await this.client.get('/raffle/statistics.json');
			return response.data;
		}

		const response: AxiosResponse<AnswerStatistics[]> = await this.client.get<AnswerStatistics[]>(
			`/consumer/raffles/${raffleId}/answer-statistics`,
			{
				validateStatus: (status: number): boolean => status === 200
			}
		);

		return response.data;
	}

	/**
	 * Retrieve information on a raffle
	 *
	 * @param raffleId
	 */
	public async getRaffle(raffleId: string): Promise<IRaffle> {
		if (RaffleService.useMock()) {
			const response = await this.client.get('/raffle/1.json');
			return response.data;
		}

		const response: AxiosResponse<IRaffle> = await this.client.get<IRaffle>(`/consumer/raffles/${raffleId}`, {
			validateStatus: (status: number): boolean => status === 200
		});

		return response.data;
	}

	private get client(): AxiosInstance {
		if (!this.clientInstance) {
			let baseURL: string = '';
			const haribo = (window as any).HARIBO || {};

			if (haribo.global && haribo.global.env && haribo.global.env.RAFFLES_BASE_URL) {
				baseURL = haribo.global.env.RAFFLES_BASE_URL;
			}

			this.clientInstance = Axios.create({
				baseURL,
				timeout: 30000
			});
		}

		return this.clientInstance;
	}

	private static handleParticipationResponseError(error: Error | AxiosError): void {
		if ((error as AxiosError).isAxiosError) {
			const axiosError: AxiosError<ParticipationResponse> = error as AxiosError;

			if (axiosError.response) {
				const response = axiosError.response;
				const data: ParticipationResponse = response.data;

				if (data.errorCode) {
					throw new RaffleServiceError(data.errorCode, data.message || undefined);
				}
			}
		}

		throw error;
	}

	private static useMock(): boolean {
		const baseURL: string = (window as any).HARIBO.global.env.RAFFLES_BASE_URL;
		return baseURL.startsWith('/assets/mock-data');
	}
}
